import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { CATALOG_FIXTURE } from '@/lib/fixtures/catalog';
import type { CatalogItem, Category } from '@/types/domain';

interface CatalogRow {
  id: string;
  qbo_item_id: number;
  name: string;
  sku: string | null;
  category: Category;
  unit: string;
  list_price: number;
  qty_on_hand: number;
  entity: 'AS' | 'FF';
  accent_color: string | null;
  tag: string | null;
  orderable: boolean;
  active: boolean;
  image_url: string | null;
  image_thumb_url: string | null;
  bib_image_url: string | null;
  description: string | null;
  sf_item_id: string | null;
  sf_item_code: string | null;
  sf_item_name: string | null;
  display_name: string | null;
  pack_size: string | null;
  volume_oz: number | string | null;
  weight_lbs: number | string | null;
  container_type: string | null;
  dimensions_in: string | null;
}

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

function rowToCatalogItem(row: CatalogRow): CatalogItem {
  return {
    id: row.id,
    qboItemId: row.qbo_item_id,
    name: row.name,
    sku: row.sku,
    category: row.category,
    unit: row.unit,
    listPrice: Number(row.list_price),
    qtyOnHand: row.qty_on_hand,
    entity: row.entity,
    accentColor: row.accent_color,
    tag: row.tag,
    orderable: row.orderable,
    active: row.active,
    imageUrl: row.image_url,
    imageThumbUrl: row.image_thumb_url,
    bibImageUrl: row.bib_image_url,
    description: row.description,
    sfItemId: row.sf_item_id,
    sfItemCode: row.sf_item_code,
    sfItemName: row.sf_item_name,
    displayName: row.display_name,
    packSize: row.pack_size,
    volumeOz: toNum(row.volume_oz),
    weightLbs: toNum(row.weight_lbs),
    containerType: row.container_type,
    dimensionsIn: row.dimensions_in,
  };
}

export function useCatalog() {
  return useQuery<CatalogItem[]>({
    queryKey: ['catalog', 'v5'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('catalog')
        .select(
          'id, qbo_item_id, name, sku, category, unit, list_price, qty_on_hand, entity, accent_color, tag, orderable, active, image_url, image_thumb_url, bib_image_url, description, sf_item_id, sf_item_code, sf_item_name, display_name, pack_size, volume_oz, weight_lbs, container_type, dimensions_in',
        )
        .eq('active', true)
        .eq('orderable', true)
        .order('category')
        .order('name');

      if (error) return CATALOG_FIXTURE;
      if (!data || data.length === 0) return CATALOG_FIXTURE;
      return (data as CatalogRow[]).map(rowToCatalogItem);
    },
    staleTime: 5 * 60 * 1000,
  });
}
